/**
 * Single source of truth for state-aware action-menu gating, shared by
 * `SeedsActions`, `AssetActions`, and the asset row menu
 * (`getAssetStatusOptions`).
 *
 * Entity encodings differ (a raw `'AL'` means *AcceptLow → approved* for a seed
 * but *ActiveLow → active* for an asset), so two entity-specific classifiers
 * absorb the difference and all downstream logic operates on a normalized,
 * entity-agnostic {@link LifecycleState}. Pure functions only — no React, no
 * rendering — mirroring the `hardDelete.ts` gating util in this directory.
 *
 * @module components/actions/constants/actionApplicability
 */
import { AssetStatus, SeedStatus } from '@/types';

/** Normalized, entity-agnostic lifecycle state. */
export type LifecycleState =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'frozen'
  | 'active'
  | 'deleted'
  | 'unknown';

/**
 * Strip the legacy `domain#`/`ip#` composite prefix (client-side statistics
 * keys). Exported so display code (`SeedStatus.tsx`) normalizes with the same
 * rule as the gating classifiers — the two must never disagree on what a raw
 * status string means.
 */
export const stripLegacyPrefix = (
  status: string | undefined
): string | undefined => status?.replace(/^(domain|ip)#/, '');

/** Target states for which a bulk eligibility note is meaningful. */
const GATEABLE_TARGET_STATES: ReadonlySet<LifecycleState> =
  new Set<LifecycleState>([
    'approved',
    'rejected',
    'frozen',
    'active',
    'deleted',
  ]);

/**
 * Classify a raw {@link SeedStatus} string (full-string encoding), first
 * stripping any legacy `domain#`/`ip#` composite prefix. Low variants collapse
 * onto their base state (`AL`≡`A` → approved, `PL`≡`P` → pending). The
 * comparison is exact so `'FR'` (Reject) never collides with `'F'` (Freeze).
 *
 * `Unavailable` ('U', shown as "Dormant") deliberately falls through to
 * `'unknown'` (fail-open): it is machine-set, self-healing, and stays
 * actionable so a user can scan/refresh to re-check resolution — the backend
 * `nonScannableStatus` gate doesn't block it either (ENG-4953). Do not map it
 * to a lifecycle classification; that would change menu-gating semantics.
 */
export function classifySeedStatus(status: string | undefined): LifecycleState {
  // Defense-in-depth: the backend status vocabulary is bare ('A', 'FR', …) —
  // the `class#status` composite exists only as client-side statistics keys
  // (see transformSeedStats) — but legacy display code stripped these
  // prefixes for years, so normalize at this single gating choke point too.
  const normalized = stripLegacyPrefix(status);
  switch (normalized) {
    case SeedStatus.Accept: // 'A'
    case SeedStatus.AcceptLow: // 'AL'
      return 'approved';
    case SeedStatus.Pending: // 'P'
    case SeedStatus.PendingLow: // 'PL'
      return 'pending';
    case SeedStatus.Reject: // 'FR'
      return 'rejected';
    case SeedStatus.Freeze: // 'F'
      return 'frozen';
    case SeedStatus.Delete: // 'D'
      return 'deleted';
    default:
      return 'unknown';
  }
}

/**
 * Classify a raw {@link AssetStatus} string (enum-value encoding). All three
 * active variants (`A`/`AL`/`AP`) collapse onto `active`; `Unavailable` (`U`)
 * is a known-but-non-gating status treated as `unknown`.
 *
 * `FrozenRejected` ('FR') maps to `rejected`, NOT `frozen` — matching
 * {@link classifySeedStatus}. Mapping it to `frozen` would flip the
 * Freeze/Unfreeze menu for an all-FR selection (hide Freeze, surface
 * Unfreeze), but the backend (`asset.Service.VerifyIfAllowedUpdate`) only
 * allows non-Praetorian status changes from exact 'F' or Active — so the
 * surfaced Unfreeze would be a guaranteed-to-fail button, while hiding Freeze
 * would remove the legitimate Praetorian FR→F path (which the ⌥F keyboard
 * shortcut still offers).
 */
export function classifyAssetStatus(
  status: string | undefined
): LifecycleState {
  switch (status) {
    case AssetStatus.Active: // 'A'
    case AssetStatus.ActiveLow: // 'AL'
    case AssetStatus.ActivePassive: // 'AP'
      return 'active';
    case AssetStatus.Frozen: // 'F'
      return 'frozen';
    case AssetStatus.FrozenRejected: // 'FR' (displayed "Rejected") — non-scannable but NOT frozen-family for menu gating
      return 'rejected';
    case AssetStatus.Pending: // 'P'
      return 'pending';
    case AssetStatus.Deleted: // 'D'
      return 'deleted';
    case AssetStatus.Unavailable: // 'U'
      return 'unknown';
    default:
      return 'unknown';
  }
}

/** Menu gating result for one selection (single row or N loaded rows). */
export interface ActionApplicability {
  /** Hide the Freeze item — every selected row is already frozen. */
  hideFreeze: boolean;
  /** Hide the Unfreeze/Activate item — no selected row is frozen. */
  hideUnfreeze: boolean;
  /**
   * Hide the Approve item — every selected row is frozen, where Approve is
   * redundant: Unfreeze and Approve send the identical Accept mutation
   * (`handleActivate` delegates to `handleApprove`). Seeds only.
   */
  hideApprove: boolean;
  /** Inline reason to disable Approve (null = enabled). Seeds only. */
  approveDisabledReason: string | null;
  /** Inline reason to disable Reject (null = enabled). Seeds only. */
  rejectDisabledReason: string | null;
}

/** Fully-permissive gating: nothing hidden, nothing disabled. */
function allPermissive(): ActionApplicability {
  return {
    hideFreeze: false,
    hideUnfreeze: false,
    hideApprove: false,
    approveDisabledReason: null,
    rejectDisabledReason: null,
  };
}

/**
 * Compute state-aware menu gating from the loaded selected rows' states. A
 * single `every()` rule unifies single-entity and uniform-bulk gating, degrades
 * to permissive on mixed selections (idempotent apply), and short-circuits to
 * permissive in select-all-matching mode.
 *
 * For a fully-frozen selection: Freeze is hidden (no-op), Approve is hidden
 * (redundant — Unfreeze sends the identical Accept mutation), and Reject stays
 * enabled (rejecting a frozen seed is a legitimate one-step transition the
 * backend accepts directly).
 *
 * @param states normalized states of loaded, explicitly-selected rows
 * @param permissive true in select-all-matching mode (or when the caller cannot
 *   see the full set) — returns all-permissive
 */
export function getActionApplicability(
  states: LifecycleState[],
  permissive = false
): ActionApplicability {
  if (permissive || states.length === 0) {
    return allPermissive();
  }

  // Frozen is a no-op iff every selected row is already frozen. Approve is
  // hidden (not disabled) because it duplicates Unfreeze; Reject stays live.
  if (states.every(s => s === 'frozen')) {
    return {
      hideFreeze: true,
      hideUnfreeze: false,
      hideApprove: true,
      approveDisabledReason: null,
      rejectDisabledReason: null,
    };
  }

  return {
    hideFreeze: false,
    // Unfreeze is only meaningful when at least one row is frozen.
    hideUnfreeze: states.every(s => s !== 'frozen'),
    hideApprove: false,
    approveDisabledReason: states.every(s => s === 'approved')
      ? 'Already approved'
      : null,
    rejectDisabledReason: states.every(s => s === 'rejected')
      ? 'Already rejected'
      : null,
  };
}

/**
 * Compute the bulk-dialog eligibility ledger
 * ("N will change · M unaffected · T selected").
 *
 * The ledger counts by RAW status equality, not by normalized {@link
 * LifecycleState}. The backend applies the requested raw status verbatim
 * (WithStatus → Merge → History.Update fires whenever the stored status differs
 * from the requested one), so a low-variant row (seed `AL`, asset `AL`/`AP`)
 * targeted with a bare `A` IS affected — it appends a history record, moves into
 * the exact-status filter bucket, and raises scan intensity (Low→Active) — even
 * though its normalized state already matches the target. Classification is used
 * ONLY for the target-state gate (`GATEABLE_TARGET_STATES`); the unaffected count
 * is pure raw equality (after stripping the legacy composite prefix).
 *
 * Returns null when nothing is already at the raw target status, when there are
 * no rows, or when the target is not a gate-able state. The `unaffected === 0`
 * early return means no note renders when every row genuinely changes (e.g. an
 * `[AL, P]` Approve, where the requested `A` differs from both) — now honest.
 *
 * `expectedTotal` is the dialog's authoritative selection count. Callers derive
 * `rawStatuses` from loaded rows ∩ selection, which can be a strict subset of
 * the true selection; a ledger computed from partial statuses would contradict
 * the dialog's own headline count, so the note is suppressed on mismatch.
 */
export function getBulkEligibilityNote(
  rawStatuses: (string | undefined)[],
  rawTargetStatus: string,
  classify: (status: string | undefined) => LifecycleState,
  expectedTotal?: number
): string | null {
  const total = rawStatuses.length;
  if (total === 0 || !GATEABLE_TARGET_STATES.has(classify(rawTargetStatus))) {
    return null;
  }
  if (expectedTotal !== undefined && expectedTotal !== total) {
    return null;
  }

  const target = stripLegacyPrefix(rawTargetStatus);
  const unaffected = rawStatuses.filter(
    s => stripLegacyPrefix(s) === target
  ).length;
  if (unaffected === 0) {
    return null;
  }

  const willChange = total - unaffected;
  return `${willChange} will change · ${unaffected} unaffected · ${total} selected`;
}
